import SoundSettings from "./SoundSettings";
import { getSoundSource } from "../sounds/catalog";
import useRoutineCompletions from "../MyPresetManager/useRoutineCompletions";
import { newRunId } from "../MyPresetManager/routineCompletions";
import CompletionSaveStatus from "../MyPresetManager/CompletionSaveStatus";
import usePreferences from "./usePreferences";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import Snackbar from "@mui/material/Snackbar";
import CelebrationRoundedIcon from "@mui/icons-material/CelebrationRounded";
import { giveMeTaskScore } from "../utils/taskCompletion";
import startTask from "../AudioFiles/startTaskCut.mp3";
import SkipNextRoundedIcon from "@mui/icons-material/SkipNextRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import { advanceTaskTimer, isTaskWarningActive } from "../utils/taskTimer";
import { playSound, unlockAudio } from "../utils/audio";
import React, { useState, useEffect, useRef, useCallback } from "react";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import EmojiFoodBeverageIcon from "@mui/icons-material/EmojiFoodBeverage";
import SettingsIcon from "@mui/icons-material/Settings";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import VibrationRoundedIcon from "@mui/icons-material/VibrationRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import ScoreRoundedIcon from "@mui/icons-material/ScoreRounded";
import TimerRoundedIcon from "@mui/icons-material/TimerRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import BoltIcon from "@mui/icons-material/Bolt";
import Checkbox from "@mui/material/Checkbox";
import Timer from "./Timer.js";
import MenuTabs from "../MenuPage/MenuTabs.js";
import { motion, AnimatePresence } from "framer-motion";
import Dashboard from "./Dashboard.js";
import { defaultTasks, levels } from "./Constants.js";
import NotListedLocationIcon from "@mui/icons-material/NotListedLocation";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import Help from "./Help.js";
import "./homepage.css";
import { limits, messages } from "../Constants/constants.js";
import {
  THEME_STORAGE_KEY,
  getAppTheme,
  themeOptions,
  themeToCssVars,
} from "../theme/themeTokens.js";
import {
  iconPopAnimation,
  iconPopAnimationSetting,
} from "./utilityFunctions.js";
const HomePage = () => {
  const { preferences, setPreference, resetPreferences } = usePreferences();
  const {
    isConfettiOn, isShowScore, isDashboardEnabled, isSoundOn, soundSelections, isVibrationOn,
    isQuickNextOn, warningTimer, visualWarningMode, isIntervalAlertEnabled,
    intervalAlertSeconds,
  } = preferences;
  const [user, setUser] = useState(null);
  const completions = useRoutineCompletions(user?.uid);
  const recordRoutineCompletion = completions.record;
  const presetRuns = useRef(new Map());
  const [totalTime, setTotalTime] = useState(0);
  const [totalSavedTime, setTotalSavedTime] = useState(0);
  const [totalBooster, setTotalBooster] = useState(0);
  const [totalBreakTime, setTotalBreakTime] = useState(0);
  const [tasks, setTasks] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("glow-panda-tasks"));
      return Array.isArray(saved) ? saved.map((task) => advanceTaskTimer(task, Date.now())) : defaultTasks;
    } catch { return defaultTasks; }
  });
  useEffect(() => {
    try { localStorage.setItem("glow-panda-tasks", JSON.stringify(tasks)); } catch { /* Storage may be unavailable. */ }
  }, [tasks]);
  const [celebration, setCelebration] = useState(0);
  const [allDoneToast, setAllDoneToast] = useState(false);
  useEffect(() => {
    if (!celebration) return undefined;
    const timeout = setTimeout(() => setCelebration(0), 3600);
    return () => clearTimeout(timeout);
  }, [celebration]);
  const [autoGroupId, setAutoGroupId] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const taskRowRefs = useRef(new Map());
  const [taskToReveal, setTaskToReveal] = useState(null);
  const revealTask = useCallback((task) => {
    if (!task) return;
    if (task.presetGroupId) {
      setCollapsedGroups((previous) => {
        const next = new Set(previous);
        next.delete(task.presetGroupId);
        return next;
      });
    }
    setTaskToReveal(task.id);
  }, []);
  useEffect(() => {
    if (taskToReveal === null) return;
    const scrollToTask = () => {
      const row = taskRowRefs.current.get(taskToReveal);
      if (row && typeof row.scrollIntoView === "function") {
        const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        row.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center", inline: "nearest" });
      }
    };
    scrollToTask();
    // Re-center after the group's rows finish their entrance animation.
    const timeout = setTimeout(scrollToTask, 320);
    return () => clearTimeout(timeout);
  }, [taskToReveal]);
  const presetGroupSequence = useRef(0);
  const [taskName, setTaskName] = useState("");
  const [estimatedTaskTimeInMin, setEstimatedTaskTimeInMin] = useState("");
  const [isSettingOn, setIsSettingOn] = useState(false);
  const [isMenuOn, setIsMenuOn] = useState(false);
  const [isHelpOn, setIsHelpOn] = useState(false);
  const [initialUserInteraction, setInitialUserInteraction] = useState(false);

  const inputRef1 = useRef(null);
  const inputRef2 = useRef(null);
  const focusFirstInput = () => {
    inputRef1.current.focus();
  };
  const screenHeight = window.innerHeight;
  const maxHeight = screenHeight - 210;
  const screenWidth = window.innerWidth;
  // Calculate the maximum width for the container
  const supportsVibration = typeof navigator.vibrate === "function";
  const [attentionGrabTimer, setAttentionGrabTimer] = useState("");
  const [rotation, setRotation] = useState(0);
  const [selectedThemeId, setSelectedThemeId] = useState(() => {
    return localStorage.getItem(THEME_STORAGE_KEY) || "light";
  });

  // Theme related here
  const selectedTheme = getAppTheme(selectedThemeId);
  const currentThemeName = selectedTheme.id;
  const themeColors = selectedTheme.colors;
  const themeVars = themeToCssVars(selectedTheme);
  const colorMode = selectedTheme.mode !== "dark";
  const homeIconColor = colorMode ? themeColors.text : themeColors.textMuted;

  const getTaskNameAndTime = () => {
    const trimmedTaskName = (taskName || "").trim();
    if (estimatedTaskTimeInMin) {
      return {
        taskName: trimmedTaskName,
        estimatedTaskTimeInMin,
      };
    }

    const trailingTimeMatch = trimmedTaskName.match(/^(.*?)(\d{1,2})$/);

    if (!trailingTimeMatch) {
      return {
        taskName: trimmedTaskName,
        estimatedTaskTimeInMin,
      };
    }

    const parsedTaskName = trailingTimeMatch[1].trim();
    const parsedTaskTime = trailingTimeMatch[2];

    return {
      taskName: parsedTaskName,
      estimatedTaskTimeInMin: parsedTaskTime,
    };
  };

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, selectedTheme.id);
    document.documentElement.dataset.theme = selectedTheme.id;
    Object.entries(themeVars).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
    document.body.style.backgroundColor = themeColors.pageBg;
    document.body.style.color = themeColors.text;
  }, [selectedTheme.id, themeColors.pageBg, themeColors.text, themeVars]);

  const addBreak = (e) => {
    iconPopAnimation(e);
    if (taskName == "Break") {
      setEstimatedTaskTimeInMin(String(Number(estimatedTaskTimeInMin) + 5));
    } else {
      setTaskName("Break");
      setEstimatedTaskTimeInMin("5");
    }
  };
  const runningTasks = tasks.filter((task) => task.isTaskRunning && !task.isCompleted && !task.isTimerLocked);
  const hasRunningTask = runningTasks.length > 0;
  const awarenessIdleSince = useRef(null);
  const showScreenWarning = visualWarningMode >= 2 && runningTasks.some((task) =>
    isTaskWarningActive(task, warningTimer));
  const rippleTask = runningTasks.find((task) => isTaskWarningActive(task, warningTimer));
  const rippleTaskId = rippleTask?.id;
  const rippleGroupId = rippleTask?.presetGroupId;
  const [rippleOrigin, setRippleOrigin] = useState({ x: 0, y: 0 });
  useEffect(() => {
    if (visualWarningMode !== 4 || rippleTaskId === undefined) return;
    const updateOrigin = () => {
      const row = taskRowRefs.current.get(rippleTaskId);
      const target = rippleGroupId && collapsedGroups.has(rippleGroupId)
        ? document.getElementById(rippleGroupId)?.previousElementSibling : row;
      if (!target) return;
      const bounds = target.getBoundingClientRect();
      setRippleOrigin({
        x: Math.max(0, Math.min(window.innerWidth, bounds.left + bounds.width / 2)),
        y: Math.max(0, Math.min(window.innerHeight, bounds.top + bounds.height / 2)),
      });
    };
    updateOrigin();
    window.addEventListener("scroll", updateOrigin, true);
    window.addEventListener("resize", updateOrigin);
    const timeout = setTimeout(updateOrigin, 350);
    return () => {
      window.removeEventListener("scroll", updateOrigin, true);
      window.removeEventListener("resize", updateOrigin);
      clearTimeout(timeout);
    };
  }, [visualWarningMode, rippleTaskId, rippleGroupId, collapsedGroups]);
  const currentQuickTask = runningTasks.length === 1 ? runningTasks[0] : null;
  const needsExtraTime = isQuickNextOn && currentQuickTask &&
    currentQuickTask.remainingTime < 30 && (currentQuickTask.snoozeTimer || 0) <= 0;


  const addBooster = (e) => {
    iconPopAnimation(e);
    const now = Date.now();
    setTasks((previous) => previous.map((task) => {
      if (isQuickNextOn && task.id !== currentQuickTask?.id) return task;
      const updated = advanceTaskTimer(task, now);
      if (updated.isCompleted || updated.remainingTime >= 30) return updated;
      return {
        ...updated,
        snoozeTimer: Math.min(600, (updated.snoozeTimer || 0) + 150),
        boosterCount: (updated.boosterCount || 0) + 1,
      };
    }));
  };

  const completeTask = useCallback((id, startNext = false) => {
    const now = Date.now();
    const current = tasks.find((task) => task.id === id && !task.isCompleted && !task.isTimerLocked);
    if (!current) return;
    const advanced = advanceTaskTimer(current, now);
    if (advanced.isTimerLocked) {
      setTasks((previous) => previous.map((task) => task.id === id ? advanced : task));
      return;
    }
    const isAuto = !!autoGroupId && current.presetGroupId === autoGroupId;
    const completed = isAuto ? { ...advanced, remainingTime: Math.max(0, advanced.remainingTime) } : advanced;
    if (isConfettiOn && current.presetGroupId && tasks.every((task) =>
      task.presetGroupId !== current.presetGroupId || task.id === id || task.isCompleted
    )) {
      setCelebration((value) => value + 1);
    }
    const next = isAuto ? tasks.find((task) => task.presetGroupId === autoGroupId && task.id !== id && !task.isCompleted && !task.isTimerLocked) : startNext ? tasks.slice(tasks.indexOf(current) + 1).find((task) => !task.isCompleted && !task.isTimerLocked) : null;
    if (isAuto && !next) setAutoGroupId(null);
    revealTask(next);
    setTasks((previous) => previous.map((task) => {
      if (task.id === id) return {
        ...completed, isTaskRunning: false, isCompleted: true,
        isTaskOverdue: completed.remainingTime < 0,
        complitionTime: completed.estimatedTaskTime - completed.remainingTime,
        taskScore: giveMeTaskScore(completed),
      };
      if (task.id === next?.id) return { ...task, isTaskRunning: true, timerUpdatedAt: now, timerRunStartedAt: now };
      return task;
    }));
    if (!completed.taskName.toLowerCase().startsWith("break")) {
      setTotalTime((value) => value + completed.estimatedTaskTime - completed.remainingTime);
      setTotalSavedTime((value) => value + completed.remainingTime > 0 ? value + completed.remainingTime : value);
      setTotalBooster((value) => value + (completed.boosterCount || 0));
    } else {
      setTotalBreakTime((value) => value + completed.estimatedTaskTime);
    }
    setInitialUserInteraction(true);
    if (isSoundOn) playSound(getSoundSource(isAuto && next ? "transition" : "completion", soundSelections[isAuto && next ? "transition" : "completion"]));
  }, [tasks, autoGroupId, isConfettiOn, isSoundOn, soundSelections, revealTask]);

  useEffect(() => {
    const due = tasks.find((task) => autoGroupId && task.presetGroupId === autoGroupId && task.isTaskRunning && !task.isCompleted && !task.isTimerLocked && task.remainingTime <= 0);
    if (due) completeTask(due.id);
  }, [tasks, autoGroupId, completeTask]);

  const toggleGroupPlayback = (group) => {
    const now = Date.now();
    const running = group.tasks.find(({ task }) => task.isTaskRunning && !task.isCompleted && !task.isTimerLocked);
    const pause = autoGroupId === group.id && !!running;
    const next = running?.task || group.tasks.find(({ task }) => !task.isCompleted && !task.isTimerLocked)?.task;
    if (!next) return;
    if (!pause) revealTask(next);
    setAutoGroupId(group.id);
    setInitialUserInteraction(true);
    if (isSoundOn) { unlockAudio(); if (!pause) playSound(startTask); }
    setTasks((previous) => previous.map((task) => {
      const updated = advanceTaskTimer(task, now);
      if (updated.isTimerLocked) return updated;
      return {
        ...updated, isTaskRunning: !pause && task.id === next.id, timerUpdatedAt: now,
        timerRunStartedAt: task.isTaskRunning ? updated.timerRunStartedAt : now,
      };
    }));
  };

  const handleQuickNext = () => {
    if (!tasks.some((task) => !task.isCompleted && !task.isTimerLocked)) {
      setAllDoneToast(true);
      return;
    }
    if (isSoundOn) unlockAudio();
    if (runningTasks.length > 1) return;
    if (currentQuickTask) {
      completeTask(currentQuickTask.id, true);
      return;
    }
    const next = tasks.find((task) => !task.isCompleted && !task.isTimerLocked);
    if (!next) return;
    revealTask(next);
    const now = Date.now();
    setTasks((previous) => previous.map((task) => task.id === next.id
      ? { ...task, isTaskRunning: true, timerUpdatedAt: now, timerRunStartedAt: now } : task));
    setInitialUserInteraction(true);
    if (isSoundOn) playSound(startTask);
  };
  const playWarningSound = useCallback((soundName) => {
    if (isSoundOn) {
      playSound(soundName);
    }
  }, [isSoundOn]);

  // This code is use to prevent website reloading
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      // This prevents the page from reloading without warning
      event.preventDefault();
      event.returnValue = ""; // Standard way to trigger a confirmation dialog in modern browsers
    };

    // Add the event listener
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Parse bracketed list input like: [Read:10, Exercise:5, Break:1]
  const parseTaskListInput = (rawInput) => {
    if (!rawInput || typeof rawInput !== "string")
      return { ok: false, error: "Empty input" };

    // remove surrounding brackets if present and trim
    const trimmed = rawInput.trim().replace(/^\[|\]$/g, "").trim();
    if (!trimmed) return { ok: false, error: "Empty list" };

    // pattern: name:number(,name:number)*
    // Accept quoted or unquoted names; capture name up to first colon, then number
    const items = trimmed.split(/\s*,\s*/);
    const MAX_LIST_ITEMS = 99; // user requirement: list length < 100
    const MAX_NAME_LEN = 500; // per-task name character limit
    const MAX_TASK_MINUTES = 99; // per-task time limit in minutes
    if (items.length > MAX_LIST_ITEMS) {
      return {
        ok: false,
        error: `List too long. Maximum ${MAX_LIST_ITEMS} tasks allowed in a list.`,
      };
    }

    const parsed = [];
    for (const item of items) {
      const m = item.match(/^(.*?):\s*(\d+)$/);
      if (!m) {
        return { ok: false, error: "Invalid item format: " + item };
      }
      let name = m[1].trim();
      // strip surrounding quotes if present
      if ((name.startsWith("'") && name.endsWith("'")) || (name.startsWith('"') && name.endsWith('"'))) {
        name = name.slice(1, -1).trim();
      }
      if (name.length > MAX_NAME_LEN) {
        return { ok: false, error: `Task name too long (max ${MAX_NAME_LEN} chars): ${name}` };
      }
      const minutes = parseInt(m[2], 10);
      if (!(minutes > 0 && minutes <= MAX_TASK_MINUTES)) {
        return {
          ok: false,
          error: `Each task time must be a number > 0 and <= ${MAX_TASK_MINUTES} minutes.`,
        };
      }

      parsed.push({
        id: Math.random(),
        taskName: name.charAt(0).toUpperCase() + name.slice(1),
        estimatedTaskTime: minutes * 60,
        remainingTime: minutes * 60,
        completionTime: 0,
        isTaskRunning: false,
        isCompleted: false,
        isTaskOverdue: false,
        snoozeTimer: 0,
        taskScore: 0,
      });
    }

    return { ok: true, tasks: parsed };
  };

  // Detect whether current taskName is a bracketed list input: starts with '[' and ends with ']'
  const isListInput = (() => {
    try {
      const t = (taskName || "").trim();
      return t.startsWith("[") && t.endsWith("]");
    } catch (e) {
      return false;
    }
  })();

  // If user entered a list input, clear the single-time input to avoid confusion
  React.useEffect(() => {
    if (isListInput) {
      setEstimatedTaskTimeInMin("");
    }
  }, [isListInput]);

  const handleAddTask = (e) => {
    setRotation((prevRotation) => prevRotation + 90);
    if (!initialUserInteraction) {
      setInitialUserInteraction(true);
    }
    const parsedTaskInput = getTaskNameAndTime();
    const nextTaskName = parsedTaskInput.taskName;
    const nextEstimatedTaskTimeInMin =
      parsedTaskInput.estimatedTaskTimeInMin;
    const customValidation =
      nextEstimatedTaskTimeInMin <= limits.maxTimeLimit &&
      tasks.length < limits.maxTasksLimit;
    const isLastTaskBreak =
      tasks[tasks.length - 1]?.taskName.toLowerCase() == "break" &&
      nextTaskName.toLowerCase() == "break";
    // detect list-style input and parse via helper
    const listParseResult = parseTaskListInput(taskName);
    if (listParseResult.ok) {
      setTasks([...tasks, ...listParseResult.tasks]);
      setTaskName("");
      setEstimatedTaskTimeInMin("");
      return;
    } else {
      // if list-like input was provided but invalid, show the error
      const looksLikeList = /\[|,|:/.test(taskName);
      if (looksLikeList && taskName.trim().length > 0) {
        alert(listParseResult.error || "Invalid list format.");
        return;
      }
    }

    if (
      nextTaskName &&
      nextEstimatedTaskTimeInMin &&
      customValidation &&
      !isLastTaskBreak
    ) {
      setTasks([
        ...tasks,
        {
          id: Math.random(),

          taskName: nextTaskName.charAt(0).toUpperCase() + nextTaskName.slice(1),
          estimatedTaskTime: parseInt(nextEstimatedTaskTimeInMin) * 60,

          remainingTime: parseInt(nextEstimatedTaskTimeInMin) * 60,
          complitionTime: 0,

          isTaskRunning: false,
          isCompleted: false,
          isTaskOverdue: false,
          snoozeTimer: 0,
          taskScore: 0,
        },
      ]);
      setTaskName("");
      setEstimatedTaskTimeInMin("");
    } else {
      if (isLastTaskBreak) {
        alert(
          messages.double_break_warning_messages[
            Math.floor(
              Math.random() * messages.double_break_warning_messages.length
            )
          ]
        );
      } else if (nextTaskName && nextEstimatedTaskTimeInMin > limits.maxTimeLimit) {
        alert(
          messages.task_time_limit_warning_messages[
            Math.floor(
              Math.random() * messages.task_time_limit_warning_messages.length
            )
          ]
        );
      } else if (tasks.length >= limits.maxTasksLimit) {
        alert(
          `You've reached the max task limit of ${limits.maxTasksLimit}! Clear some tasks to add more.`
        );
      } else {
        alert("Please enter task name and estimated task time.");
      }
    }
  };

  // This is for Attention Grab Timer
  useEffect(() => {
    let timer;

    if (parseFloat(attentionGrabTimer) > 0.1) {
      const delay = parseFloat(attentionGrabTimer) * 60 * 1000; // Convert minutes to milliseconds

      timer = setInterval(() => {
        console.log("Here is time", new Date());
        playWarningSound(getSoundSource("awareness", soundSelections.awareness));
      }, delay);
    }

    return () => clearInterval(timer); // Clean up the timeout on component unmount or if timer restarts
  }, [attentionGrabTimer, playWarningSound, soundSelections.awareness]);

  useEffect(() => {
    const intervalSeconds = parseFloat(intervalAlertSeconds);
    if (hasRunningTask || !isIntervalAlertEnabled) {
      awarenessIdleSince.current = null;
    } else if (awarenessIdleSince.current === null) {
      awarenessIdleSince.current = Date.now();
    }
    const idleDeadline = awarenessIdleSince.current === null
      ? Infinity : awarenessIdleSince.current + 60 * 60 * 1000;

    if (
      !isIntervalAlertEnabled ||
      !isSoundOn ||
      !initialUserInteraction ||
      !(intervalSeconds > 0) ||
      Date.now() >= idleDeadline
    ) {
      return undefined;
    }

    const timer = setInterval(() => {
      if (Date.now() >= idleDeadline) {
        clearInterval(timer);
        return;
      }
      playWarningSound(getSoundSource("awareness", soundSelections.awareness));
    }, intervalSeconds * 1000);
    const idleTimeout = Number.isFinite(idleDeadline)
      ? setTimeout(() => clearInterval(timer), Math.max(0, idleDeadline - Date.now()))
      : null;

    return () => {
      clearInterval(timer);
      clearTimeout(idleTimeout);
    };
  }, [
    hasRunningTask,
    isIntervalAlertEnabled,
    intervalAlertSeconds,
    isSoundOn,
    initialUserInteraction,
    playWarningSound,
    soundSelections.awareness,
  ]);

  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      setTasks((prevTasks) => prevTasks.map((task) => advanceTaskTimer(task, now)));
    };
    tick();
    const interval = setInterval(tick, 1000);
    const handleVisibility = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  const getUserLevel = (tasks, score) => {
    let activeCount = 0;
    let overdueTasks = 0;
    const totalTasks = tasks.length;

    tasks.forEach((task) => {
      activeCount += task.estimatedTaskTime - task.remainingTime;
      if (task.isTaskOverdue) overdueTasks++;
    });

    // Calculate overdue percentage
    const overduePercentage = (overdueTasks / totalTasks) * 100;

    // Determine the user's level
    for (const level of levels) {
      const {
        minActiveCount = 0,
        maxOverduePercentage = 100,
        minScore = 0,
      } = level.criteria;

      if (
        activeCount >= minActiveCount &&
        overduePercentage <= maxOverduePercentage &&
        score >= minScore
      ) {
        return level.name;
      }
    }

    // Default level if no criteria match
    return "";
  };
  const handleAddItem = (description) => {
    setTaskName(description);
    setIsMenuOn(false);
  };

  useEffect(() => {
    for (const run of presetRuns.current.values()) {
      if (run.recorded || run.uid !== user?.uid) continue;
      tasks.forEach((task) => { if (task.isCompleted && run.taskIds.includes(task.id)) run.completedIds.add(task.id); });
      if (run.taskIds.length && run.taskIds.every((id) => run.completedIds.has(id))) {
        run.recorded = true;
        recordRoutineCompletion?.({ runId: run.runId, presetId: run.presetId });
      }
    }
  }, [tasks, user?.uid, recordRoutineCompletion]);

  const addTasksToList = (preset) => {
    const presetGroupId = `preset-group-${++presetGroupSequence.current}`;
    const runId = newRunId();
    const presetTasks = (preset?.presetTasks || []).map((task) => ({
      id: Math.random(),
      presetGroupId,
      presetRunId: runId,
      presetOwnerUid: user?.uid || null,
      presetGroupName: preset.presetName || "Preset",
      taskName: task.taskName,
      estimatedTaskTime: Math.round(Number(task.estimatedTaskTime) * 60),
      remainingTime: Math.round(Number(task.estimatedTaskTime) * 60),
      completionTime: 0,
      isTaskRunning: false,
      isCompleted: false,
      isTaskOverdue: false,
      snoozeTimer: 0,
      taskScore: 0,
    }));
    if (preset.isDailyRoutine && preset.id && user?.uid && presetTasks.length) {
      presetRuns.current.set(runId, { runId, presetId: preset.id, uid: user.uid, taskIds: presetTasks.map((task) => task.id), completedIds: new Set(), recorded: false });
    }
    setTasks((previous) => [...previous, ...presetTasks]);
  };

  const taskEntries = [];
  const presetGroups = new Map();
  tasks.forEach((task, index) => {
    if (!task.presetGroupId) {
      taskEntries.push({ task, index });
      return;
    }
    let group = presetGroups.get(task.presetGroupId);
    if (!group) {
      group = { id: task.presetGroupId, name: task.presetGroupName, tasks: [] };
      presetGroups.set(group.id, group);
      taskEntries.push(group);
    }
    group.tasks.push({ task, index });
  });

  presetGroups.forEach((group) => {
    group.isComplete = group.tasks.every(({ task }) => task.isCompleted);
  });

  const togglePresetGroup = (groupId) => {
    setCollapsedGroups((previous) => {
      const next = new Set(previous);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const renderTask = ({ task, index }) => (
    <motion.li
      key={task.id}
      ref={(row) => {
        if (row) taskRowRefs.current.set(task.id, row);
        else taskRowRefs.current.delete(task.id);
      }}
      initial={{ opacity: 0, y: -20 }} // Start from above and invisible
      animate={{ opacity: 1, y: 0 }} // End in normal position with full opacity
      exit={{ opacity: 0, y: 20 }} // Fade out and move down when removed
      transition={{ duration: 0.3 }} // Smooth transition for the animation
    >
      <Timer
        taskIndex={index}
        tasks={tasks}
        setTasks={setTasks}
        onCompleteTask={completeTask}
        isAutoMode={!!autoGroupId && task.presetGroupId === autoGroupId}
        onManualPlay={() => {
          setAutoGroupId(null);
          const now = Date.now();
          setTasks((previous) => previous.map((item) => autoGroupId && item.id !== task.id && item.presetGroupId === autoGroupId
            ? { ...advanceTaskTimer(item, now), isTaskRunning: false } : item));
        }}
        totalTime={totalTime}
        totalSavedTime={totalSavedTime}
        totalBooster={totalBooster}
        totalBreakTime={totalBreakTime}
        setTotalTime={setTotalTime}
        setTotalSavedTime={setTotalSavedTime}
        setTotalBooster={setTotalBooster}
        setTotalBreakTime={setTotalBreakTime}
        isSoundOn={isSoundOn}
        soundSelections={soundSelections}
        isVibrationOn={isVibrationOn}
        isTaskHighlighed={visualWarningMode > 0}
        warningTimer={warningTimer}
        initialUserInteraction={initialUserInteraction}
        setInitialUserInteraction={setInitialUserInteraction}
        currentThemeName={currentThemeName}
      />
    </motion.li>
  );

  // Copy current tasks into the requested list string and copy to clipboard
  // Returns true on success, false on failure
  const copyTasksToClipboard = async () => {
    if (!tasks || tasks.length === 0) {
      // nothing to copy
      return false;
    }
    try {
      const parts = tasks.map((t) => {
        const minutes = Math.round((t.estimatedTaskTime || t.remainingTime) / 60);
        const name = String(t.taskName).replace(/'/g, "\\'");
        return `'${name}':${minutes}`;
      });
      const out = '[' + parts.join(',') + ']';
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(out);
          return true;
        } catch (e) {
          // fallback to prompt
          window.prompt('Copy tasks', out);
          return false;
        }
      } else {
        window.prompt('Copy tasks', out);
        return false;
      }
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const [copySuccess, setCopySuccess] = useState(false);

  //  Level code end here
  const [isAnimating, setIsAnimating] = useState(false);
  const totalTasksScore = tasks.reduce((sum, task) => sum + task.taskScore, 0);

  useEffect(() => {
    console.log(taskName);
    const handleKeyPress = (event) => {
      if (event.key === "Escape") {
        // Focus on the first input
        focusFirstInput();
      }
      if (event.key === "Enter") {
        handleAddTask();
        focusFirstInput();
      }
    };

    document.addEventListener("keydown", handleKeyPress);

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  });

  return (
    <div>
      {/* Entire Page */}
      <div
        className={`home-page-shell ${isQuickNextOn ? "has-quick-next" : ""}`}
        style={{
          ...themeVars,
          margin: "0 auto",
          textAlign: "center",
          width: "100%",
          height: "100vh",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: themeColors.pageBg,
          color: themeColors.text,
        }}
      >
        <span
          style={{ marginTop: "0px", color: themeColors.text }}
        >
          {/* ... Craft */}
        </span>
        {isDashboardEnabled && (
          <Dashboard
            colorMode={colorMode}
            totalTime={totalTime}
            totalSavedTime={totalSavedTime}
            totalBooster={totalBooster}
            totalBreakTime={totalBreakTime}
          ></Dashboard>
        )}
        {/* Leves & score panel */}
        {!isDashboardEnabled && (
          <>
            {/* <Login /> */}
            <h1
              style={{
                fontFamily: "'Cinzel', serif",
                fontSize: "2rem",
                color: themeColors.text,
                fontWeight: "700",
                letterSpacing: "0.1rem",
                paddingTop: "20px",
                margin: "0px",
              }}
            >
              Gl<span className="glow">o</span>w Panda
              {/* lazy leap */}
            </h1>

            <div className="dashboard-dimensions" style={{ display: "flex" }}>
              <label className="label-animation">
                <span>
                  Level main: {" "}
                  <span className="label-animation-level-name">
                    {getUserLevel(tasks, parseInt(totalTasksScore.toFixed(2)))}
                  </span>
                </span>
              </label>
              {isShowScore && (
                <>
                  <div
                    className="separator"
                    style={{
                      width: "1px",
                      height: "1rem",
                      backgroundColor: themeColors.border,
                    }}
                  ></div>
                  <label
                    style={{
                      fontSize: "0.9rem", // Small font size for minimal impact
                      color: themeColors.textFaint,
                      // display: "flex",
                      // alignItems: "center",
                      padding: "0.2rem 0.4rem", // Compact padding
                      letterSpacing: "0.1px", // Minimal letter-spacing
                    }}
                  >
                    <span style={{ fontSize: "0.8rem" }}>
                      Score : {totalTasksScore.toFixed(2)}
                    </span>
                  </label>
                </>
              )}
            </div>
          </>
        )}
        {/* Input panel */}
        <div className="align-task-info-center">
          <input
            type="text"
            ref={inputRef1}
            placeholder="Enter Task Name"
            value={taskName}
            className="task-name-input"
            onChange={(e) => {
              const rawValue = e.target.value;
              // Limit input to limits.maxInputCharacters (1 lakh)
              const maxChars = limits.maxInputCharacters;
              const sanitizedValue = rawValue.slice(0, maxChars);
              if (rawValue.length > maxChars) {
                alert("Max character limit: " + maxChars);
              }
              setTaskName(sanitizedValue);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                event.stopPropagation();
                handleAddTask();
                focusFirstInput();
              }
            }}
          />
          {!isListInput && (
            <input
              type="number"
              placeholder="Time"
              value={estimatedTaskTimeInMin}
              ref={inputRef2}
              className="task-time-input"
              onChange={(e) => {
                const value = Math.max(0, Math.min(99, Number(e.target.value))); // Clamp value between 0 and 99
                setEstimatedTaskTimeInMin(value);
              }}
            />
          )}

          <div
            key={`add-task-${selectedTheme.id}`}
            className="add-task-icon"
          >
            <AddIcon
              onClick={handleAddTask}
              className="bolt-icon"
              style={{
                fontSize: "37px",
                color: homeIconColor,
                alignContent: "center",
                marginTop: "2px",
                transform: `rotate(${rotation}deg)`, // Apply the rotation
                transition: "transform .3s ease-in-out", // Smooth transition
              }}
            />
          </div>
          {/* Copy Tasks moved to Settings panel */}
        </div>
        {/* Icons panel */}
        <div className={`task-toolbar ${needsExtraTime ? "has-pulsing-booster" : ""}`} style={{ marginTop: "10px" }}>
          <MenuOpenIcon
            onClick={(e) => {
              iconPopAnimation(e);
              setIsMenuOn(!isMenuOn);
              setIsHelpOn(false);
            }}
            style={{ fontSize: "32px", color: homeIconColor }}
            className="bolt-icon quick-icon-before"
          ></MenuOpenIcon>
          <EmojiFoodBeverageIcon
            className="quick-icon-before"
            onClick={addBreak}
            style={{ fontSize: "28px", color: homeIconColor }}
          ></EmojiFoodBeverageIcon>

          <span className="quick-booster-anchor">
            <button type="button"
              className={`quick-booster-button ${needsExtraTime ? "needs-extra-time" : ""}`}
              onClick={addBooster}
              disabled={isQuickNextOn && !currentQuickTask}
              aria-label="Add extra time"
              title="Add 2½ minutes of extra time">
              <BoltIcon className="bolt-icon" />
            </button>
          </span>
          <SettingsIcon
            onClick={(e) => {
              iconPopAnimationSetting(e);
              setIsSettingOn(!isSettingOn);
              // Trigger animation
              setIsAnimating(true);

              // Remove the animation class after the animation duration (0.5s)
              setTimeout(() => setIsAnimating(false), 500);
            }}
            style={{ fontSize: "28px", color: homeIconColor }}
            className="quick-icon-after"
          ></SettingsIcon>
          <NotListedLocationIcon
            onClick={(e) => {
              iconPopAnimation(e);
              setIsHelpOn(!isHelpOn);
              setIsMenuOn(false);
            }}
            style={{ fontSize: "28px", color: homeIconColor }}
            className="bolt-icon bolt-icon1 quick-icon-after"
          ></NotListedLocationIcon>
        </div>

        {/* Menu panel */}
        {isMenuOn && (
          <div
            className="menu-overlay"
            onClick={() => setIsMenuOn(false)}
            role="presentation"
          >
            <div
              className="menu-overlay-panel"
              role="dialog"
              aria-modal="true"
              aria-label="Task menu"
              onClick={(event) => event.stopPropagation()}
            >
              <MenuTabs
                setTaskName={setTaskName}
                setIsMenuOn={setIsMenuOn}
                addTasksToList={addTasksToList}
                user={user}
                setUser={setUser}
                tasks={tasks}
              ></MenuTabs>
            </div>
          </div>
        )}

        {isQuickNextOn && !isSettingOn && !isMenuOn && !isHelpOn && (
          <button type="button" className="quick-next-button"
            onClick={handleQuickNext}
            disabled={runningTasks.length > 1}
            aria-label={currentQuickTask ? "Complete current task and start next" : "Start next task"}
            title={runningTasks.length > 1 ? "Pause other tasks to use this shortcut" :
              currentQuickTask ? "Complete current task and start next" : "Start next task"}>
            <SkipNextRoundedIcon />
          </button>
        )}

        {showScreenWarning && (
          visualWarningMode === 4 ? (
            <div className="task-water-ripple" aria-hidden="true"
              style={{ "--ripple-x": `${rippleOrigin.x}px`, "--ripple-y": `${rippleOrigin.y}px` }}>
              {[0, 1, 2].map((wave) => <span key={wave} style={{ "--wave-delay": `${wave * 0.9}s` }} />)}
            </div>
          ) : <div className={`screen-task-warning ${visualWarningMode === 3 ? "is-strong" : "is-light"}`}
            aria-hidden="true" />
        )}
        {isConfettiOn && celebration > 0 && (
          <div key={celebration} className="screen-completion-confetti" aria-hidden="true">
            {isConfettiOn && Array.from({ length: 64 }, (_, index) => (
              <i key={index} style={{
                "--confetti-x": `${(index * 37) % 100}%`,
                "--confetti-delay": `${(index % 8) * 0.08}s`,
                "--confetti-duration": `${2.2 + (index % 5) * 0.15}s`,
                "--confetti-drift": `${((index * 23) % 160) - 80}px`,
                "--confetti-turn": `${index % 2 ? 540 : -540}deg`,
                "--confetti-color": ["#4ade80", "#fbbf24", "#a78bfa", "#38bdf8"][index % 4],
              }} />
            ))}
          </div>
        )}

        <Snackbar
          className="all-done-toast"
          open={allDoneToast}
          autoHideDuration={3000}
          onClose={() => setAllDoneToast(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          message="All tasks are complete"
          ContentProps={{ className: "all-done-toast-content" }}
        />

        {/* setting panel */}
        {isSettingOn && (
          <>
            <div
              className="background-overlay"
              onClick={() => setIsSettingOn(false)}
            ></div>
            <div
              className={`setting-panel ${
                isSettingOn ? "slide-in" : "slide-out"
              }`}
            >
              <header className="settings-modal-header">
                <div>
                  <span className="settings-modal-icon">
                    <SettingsIcon />
                  </span>
                  <div>
                    <h2>Settings</h2>
                    <p>Customize your focus experience.</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="settings-close-button"
                  onClick={() => setIsSettingOn(false)}
                  aria-label="Close settings"
                >
                  <CloseRoundedIcon />
                </button>
              </header>

              <div className="settings-modal-content">
                <section className="settings-group">
                  <h3>Preferences</h3>

                  <div className="settings-row settings-theme-row">
                    <span className="settings-row-icon">
                      <PaletteRoundedIcon />
                    </span>
                    <span className="settings-row-copy">
                      <strong>Theme</strong>
                      <small>Choose the visual style for the app.</small>
                    </span>
                    <div className="settings-theme-options" role="group" aria-label="Theme">
                      {themeOptions.map((themeOption) => (
                        <button
                          key={themeOption.id}
                          type="button"
                          className={`settings-theme-option ${
                            selectedTheme.id === themeOption.id ? "is-active" : ""
                          }`}
                          onClick={() => setSelectedThemeId(themeOption.id)}
                        >
                          <span
                            aria-hidden="true"
                            style={{ backgroundColor: themeOption.colors.accent }}
                          ></span>
                          {themeOption.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <label className="settings-row">
                    <span className="settings-row-icon">
                      <DashboardRoundedIcon />
                    </span>
                    <span className="settings-row-copy">
                      <strong>Activity summary</strong>
                      <small>Show your progress dashboard.</small>
                    </span>
                  <Checkbox
                    checked={isDashboardEnabled}
                    inputProps={{ "aria-label": "Activity summary" }}
                    onChange={(event) => {
                      setPreference("isDashboardEnabled", event.target.checked);
                    }}
                  />
                  </label>

                  <label className="settings-row">
                    <span className="settings-row-icon">
                      <NotificationsActiveRoundedIcon />
                    </span>
                    <span className="settings-row-copy">
                      <strong>Visual task warning</strong>
                      <small>Warn below 20 seconds and while overdue.</small>
                    </span>
                    <select className="settings-warning-select" aria-label="Visual task warning"
                      value={visualWarningMode}
                      onChange={(event) => setPreference("visualWarningMode", Number(event.target.value))}>
                      <option value={0}>No red color</option>
                      <option value={1}>Task only</option>
                      <option value={2}>Task + screen · light</option>
                      <option value={3}>Task + screen · strong</option>
                      <option value={4}>Task + spreading water ripple</option>
                    </select>
                  </label>



                  <label className="settings-row">
                    <span className="settings-row-icon"><CelebrationRoundedIcon /></span>
                    <span className="settings-row-copy">
                      <strong>Confetti celebration</strong>
                      <small>Show confetti and a green glow when a group is complete.</small>
                    </span>
                    <Checkbox checked={isConfettiOn}
                      inputProps={{ "aria-label": "Confetti celebration" }}
                      onChange={(event) => {
                        setPreference("isConfettiOn", event.target.checked);
                        if (!event.target.checked) setCelebration(0);
                      }} />
                  </label>

                  <label className="settings-row">
                    <span className="settings-row-icon"><PlayArrowIcon /></span>
                    <span className="settings-row-copy">
                      <strong>Complete &amp; Start Next</strong>
                      <small>Show a bottom shortcut and highlight extra time near zero.</small>
                    </span>
                    <Checkbox checked={isQuickNextOn}
                      inputProps={{ "aria-label": "Complete & Start Next" }}
                      onChange={(event) => setPreference("isQuickNextOn", event.target.checked)} />
                  </label>

                  <label className="settings-row">
                    <span className="settings-row-icon">
                      <VibrationRoundedIcon />
                    </span>
                    <span className="settings-row-copy">
                      <strong>Vibration alerts</strong>
                      <small>{supportsVibration
                        ? "Vibrate at zero when no purchased time remains."
                        : "Vibration is unavailable in this browser."}</small>
                    </span>
                    <Checkbox
                      checked={isVibrationOn}
                      disabled={!supportsVibration}
                      inputProps={{ "aria-label": "Vibration alerts" }}
                      onChange={(event) => {
                        setPreference("isVibrationOn", event.target.checked);
                        setInitialUserInteraction(true);
                        if (!event.target.checked && supportsVibration) navigator.vibrate(0);
                      }}
                    />
                  </label>

                  <label className="settings-row">
                    <span className="settings-row-icon">
                      <ScoreRoundedIcon />
                    </span>
                    <span className="settings-row-copy">
                      <strong>Show score</strong>
                      <small>Display your score near the current level.</small>
                    </span>
                  <Checkbox
                    checked={isShowScore}
                    inputProps={{ "aria-label": "Show score" }}
                    onChange={(event) => {
                      setPreference("isShowScore", event.target.checked);
                    }}
                  />
                  </label>
                </section>

                <SoundSettings selections={soundSelections} enabled={isSoundOn}
                  onChange={(category, selected) => setPreference("soundSelections", { ...soundSelections, [category]: selected })}>
                  <label className="settings-row">
                    <span className="settings-row-icon">
                      <VolumeUpRoundedIcon />
                    </span>
                    <span className="settings-row-copy">
                      <strong>Sound alerts</strong>
                      <small>Play audio cues during sessions.</small>
                    </span>
                  <Checkbox
                    checked={isSoundOn}
                    inputProps={{ "aria-label": "Sound alerts" }}
                    onChange={(event) => {
                      setPreference("isSoundOn", event.target.checked);
                      if (event.target.checked) {
                        setInitialUserInteraction(true);
                        unlockAudio();
                      }
                    }}
                  />
                  </label>
                </SoundSettings>
                <section className="settings-group">
                  <h3>Timer and tasks</h3>

                  <div className="settings-row">
                    <span className="settings-row-icon">
                      <TimerRoundedIcon />
                    </span>
                    <span className="settings-row-copy">
                      <strong>Countdown warning</strong>
                      <small>Seconds before sound and visual warnings begin.</small>
                    </span>
                  <input
                    type="number"
                    value={warningTimer}
                    onChange={(e) => setPreference("warningTimer", Math.max(0, Number(e.target.value)))}
                    className="settings-number-input"
                    min="0"
                    aria-label="Countdown warning seconds"
                  />
                  </div>

                  <label className="settings-row">
                    <span className="settings-row-icon">
                      <NotificationsActiveRoundedIcon />
                    </span>
                    <span className="settings-row-copy">
                      <strong>Awareness Bell</strong>
                      <small>Repeat a sound every custom seconds.</small>
                    </span>
                    <div className="settings-inline-controls">
                      <input
                        type="number"
                        value={intervalAlertSeconds}
                        onChange={(e) => {
                          const nextValue = Math.max(
                            5,
                            Math.min(999, Number(e.target.value))
                          );
                          setPreference("intervalAlertSeconds", nextValue);
                        }}
                        className="settings-number-input"
                        min="5"
                        max="999"
                        aria-label="Interval alert seconds"
                        disabled={!isIntervalAlertEnabled}
                      />
                      <Checkbox
                        checked={isIntervalAlertEnabled}
                        inputProps={{ "aria-label": "Awareness Bell" }}
                        onChange={(event) => {
                          setPreference("isIntervalAlertEnabled", event.target.checked);
                          if (!initialUserInteraction) {
                            setInitialUserInteraction(true);
                          }
                        }}
                      />
                    </div>
                  </label>

                  <div className="settings-row">
                    <span className="settings-row-icon">
                      <ContentCopyRoundedIcon />
                    </span>
                    <span className="settings-row-copy">
                      <strong>Copy task list</strong>
                      <small>Copy all current tasks in preset format.</small>
                    </span>
                    <button
                      type="button"
                      aria-label="Copy current tasks"
                      onClick={async () => {
                        const ok = await copyTasksToClipboard();
                        if (ok) {
                          setCopySuccess(true);
                          setTimeout(() => setCopySuccess(false), 1500);
                        }
                      }}
                      className={`settings-copy-button ${
                        copySuccess ? "is-success" : ""
                      }`}
                    >
                      {copySuccess ? <CheckIcon /> : <ContentCopyIcon />}
                      <span>{copySuccess ? "Copied" : "Copy"}</span>
                    </button>
                  </div>
                </section>

                <section className="settings-group">
                  <h3>Reset settings</h3>
                  <div className="settings-row">
                    <span className="settings-row-icon"><RestartAltRoundedIcon /></span>
                    <span className="settings-row-copy">
                      <strong>Restore default settings</strong>
                      <small>Reset preferences, timer settings, and theme. Your tasks and presets stay saved.</small>
                    </span>
                    <button
                      type="button"
                      className="settings-copy-button"
                      onClick={() => {
                        resetPreferences();
                        setSelectedThemeId("light");
                        setCelebration(0);
                        if (supportsVibration) navigator.vibrate(0);
                      }}
                    >
                      <RestartAltRoundedIcon />
                      <span>Reset settings</span>
                    </button>
                  </div>
                </section>
              </div>
            </div>
          </>
        )}
        {/* <div
          className="list-centered-container"
          style={{
            width: "30%", // 25% of the parent width
            height: "2px", // Thickness of the hidden line
            // backgroundColor: "transparent", // Make the line invisible
            boxShadow: "0 -1px 10px rgba(0, 0, 0, 0.9)", // Subtle downward shadow
          }}
        ></div> */}
        {/* Help Panel */}
        {isHelpOn && (
          <Help
            onClose={() => {
              setIsHelpOn(false);
            }}
          ></Help>
        )}
        {/* List panel */}
        {!isHelpOn && (
          <div
            className="list-centered-container"
            style={{
              maxHeight: `${maxHeight}px`, // Keep maxHeight dynamic
            }}
          >
            {/* List Render */}

            <ul className="main-task-list">
              <AnimatePresence>
                {taskEntries.map((entry) =>
                  entry.tasks ? (
                    <li key={entry.id} className="main-preset-group">
                      <div className={`main-preset-group-header ${entry.isComplete ? "is-complete" : ""}`}>
                        <button type="button" className={`main-preset-group-control main-preset-group-play ${autoGroupId === entry.id && entry.tasks.some(({ task }) => task.isTaskRunning) ? "is-running" : ""}`} disabled={entry.isComplete}
                          title={autoGroupId === entry.id && entry.tasks.some(({ task }) => task.isTaskRunning) ? "Group running — pause sequence" : "Start group automatically"}
                          aria-label={`${autoGroupId === entry.id && entry.tasks.some(({ task }) => task.isTaskRunning) ? "Pause" : "Play"} ${entry.name} automatically`}
                          onClick={() => toggleGroupPlayback(entry)}>
                          <PlaylistPlayIcon />
                        </button>
                        <div className="main-preset-group-toggle">
                          <span className="main-preset-group-copy">
                            <span className="main-preset-group-name" title={entry.name}>{entry.name}</span>
                            <span className="main-preset-group-stats">
                              {entry.tasks.length} {entry.tasks.length === 1 ? "task" : "tasks"}
                              {" · "}
                              {entry.isComplete ? "All done!" : `${Math.ceil(entry.tasks.reduce(
                                (seconds, { task }) => seconds + (task.isCompleted ? 0 : Math.max(0, task.remainingTime)),
                                0
                              ) / 60)} min left`}
                            </span>
                          </span>
                        </div>
                        {entry.tasks[0].task.presetOwnerUid === user?.uid && (
                          <CompletionSaveStatus name={entry.name}
                            status={completions.status(entry.tasks[0].task.presetRunId)}
                            error={completions.errors?.[entry.tasks[0].task.presetRunId]}
                            retry={() => completions.retry(entry.tasks[0].task.presetRunId)} />
                        )}
                        <span className="main-preset-group-mode">{entry.isComplete ? "Done" : autoGroupId === entry.id ? "Auto" : "Manual"}</span>
                        <button
                          type="button"
                          className="main-preset-group-remove"
                          aria-label={`Remove ${entry.name} group and all its tasks`}
                          title="Remove group"
                          onClick={() => {
                            if (!window.confirm(`Remove ${entry.name} and all its tasks?`)) return;
                            if (autoGroupId === entry.id) setAutoGroupId(null);
                            setTasks((previous) => previous.filter(
                              (task) => task.presetGroupId !== entry.id
                            ));
                            setCollapsedGroups((previous) => {
                              const next = new Set(previous);
                              next.delete(entry.id);
                              return next;
                            });
                          }}
                        >
                          <DeleteOutlineRoundedIcon />
                        </button>
                        <button type="button" className="main-preset-group-control main-preset-group-expand"
                          aria-label={`Expand or collapse ${entry.name}`} onClick={() => togglePresetGroup(entry.id)}
                          aria-expanded={!collapsedGroups.has(entry.id)} aria-controls={entry.id}>
                          <ExpandMoreRoundedIcon className={collapsedGroups.has(entry.id) ? "is-collapsed" : ""} />
                        </button>
                      </div>
                      <ul id={entry.id} hidden={collapsedGroups.has(entry.id)}>
                        <AnimatePresence>
                          {entry.tasks.map(renderTask)}
                        </AnimatePresence>
                      </ul>
                      {!collapsedGroups.has(entry.id) && (
                        <div className="main-preset-group-end" aria-hidden="true">
                          <span />
                          <span />
                          <span />
                        </div>
                      )}
                    </li>
                  ) : renderTask(entry)
                )}
              </AnimatePresence>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;
